import React from 'react';
import PropTypes from 'prop-types';
import Image from "../image";

const ImageWithAnnotations = ({
                                src,
                                alt,
                                showInLightBox,
                                onClick,
                                denseCaptions,
                                smartCrops,
                                peopleResult,
                                objectsResult,
                                readResult,
                                imageSize
                              }) => {
  const [imgWidth, imgHeight] = imageSize.split('x').map(Number);
  const scaleX = (containerWidth) => containerWidth / imgWidth;
  const scaleY = (containerHeight) => containerHeight / imgHeight;

  const containerRef = React.useRef(null);
  const [containerSize, setContainerSize] = React.useState({ width: 1, height: 1 });


  const handleImageLoad = () => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerSize({ width, height });
    }
  };

  const renderBoxes = () => {
    if (!denseCaptions || !Array.isArray(denseCaptions.values)) return null;

    const sx = scaleX(containerSize.width);
    const sy = scaleY(containerSize.height);

    return denseCaptions.values.map((item, index) => {
      const { boundingBox, text } = item;
      const style = {
        position: 'absolute',
        left: boundingBox.x * sx,
        top: boundingBox.y * sy,
        width: boundingBox.w * sx,
        height: boundingBox.h * sy,
        border: '2px solid rgba(0, 255, 0, 0.7)',
        color: 'white',
        fontSize: '0.75rem',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        zIndex: 1,
      };
      return (
        <div key={`caption-${index}`} style={style}>
          <div
            style={{
              backgroundColor: 'rgba(0, 128, 0, 0.7)',
              padding: '2px 4px',
              position: 'absolute',
              top: '-1.5em',
              left: 0,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {text}
          </div>
        </div>
      );
    });
  };


  const CONFIDENCE_THRESHOLD = 0.01;

  const renderSmartCrop = () => {
    if (!smartCrops) {
      console.log('No smartCrops data available');
      return null;
    }

    const sx = scaleX(containerSize.width);
    const sy = scaleY(containerSize.height);

    // Získajme hodnoty priamo z objektu smartCrops
    const { x, y, w, h } = smartCrops;

    // Ak sú hodnoty platné (neberieme do úvahy, ak sú mimo obrazovky)
    if (x !== undefined && y !== undefined && w !== undefined && h !== undefined) {
      const style = {
        position: 'absolute',
        left: x * sx,
        top: y * sy,
        width: w * sx,
        height: h * sy,
        border: '3px solid yellow',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        zIndex: 1,
      };

      return <div key="smartcrop" style={style} />;
    }
      console.log('Invalid smartCrops data');
      return null;

  };




  const renderPeopleBoxes = () => {
    if (!peopleResult?.length) return null;

    const sx = scaleX(containerSize.width);
    const sy = scaleY(containerSize.height);

    return peopleResult
      .filter(person => person.confidence === undefined || person.confidence >= CONFIDENCE_THRESHOLD)
      .map((person, index) => {
        const { boundingBox } = person;
        return (
          <div
            key={`person-${index}`}
            style={{
              position: 'absolute',
              left: boundingBox.x * sx,
              top: boundingBox.y * sy,
              width: boundingBox.w * sx,
              height: boundingBox.h * sy,
              border: '2px solid orange',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        );
      });
  };

  const renderObjectBoxes = () => {
    if (!objectsResult?.length) return null;

    const sx = scaleX(containerSize.width);
    const sy = scaleY(containerSize.height);
    const MIN_CONFIDENCE = 0.5;

    return objectsResult
      .filter(obj => obj.tags?.[0]?.confidence >= MIN_CONFIDENCE)
      .map((obj, index) => {
        const { boundingBox, tags } = obj;
        const tagName = tags?.[0]?.name ?? 'unknown';

        return (
          <div
            key={`object-${index}`}
            style={{
              position: 'absolute',
              left: boundingBox.x * sx,
              top: boundingBox.y * sy,
              width: boundingBox.w * sx,
              height: boundingBox.h * sy,
              border: '2px solid blue',
              boxSizing: 'border-box',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: 'rgba(0, 0, 255, 0.7)',
                color: 'white',
                fontSize: '12px',
                padding: '2px 4px',
                fontWeight: 'bold',
                zIndex: 2,
              }}
            >
              {tagName}
            </div>
          </div>
        );
      });
  };



  const renderReadResults = () => {
    if (!Array.isArray(readResult) || !readResult.length) return null;

    const sx = scaleX(containerSize.width);
    const sy = scaleY(containerSize.height);

    return readResult.flatMap((page, pageIndex) =>
      page.lines.map((line, lineIndex) => {
        const [x1, y1] = [line.boundingPolygon[0].x, line.boundingPolygon[0].y];
        const [x2, y2] = [line.boundingPolygon[2].x, line.boundingPolygon[2].y];
        const left = x1 * sx;
        const top = y1 * sy;
        const width = (x2 - x1) * sx;
        const height = (y2 - y1) * sy;

        return (
          <div
            key={`read-line-${pageIndex}-${lineIndex}`}
            style={{
              position: 'absolute',
              left,
              top,
              width,
              height,
              pointerEvents: 'none',
              color: 'black', // Text color
              fontSize: '0.8rem',

              zIndex: 5,
              overflow: 'visible', // Allow the text to extend
              whiteSpace: 'normal', // Allow text wrapping
            }}
          >
            <div
              style={{
                display: 'inline-block', // Ensure it doesn't overflow
                backgroundColor: 'white', // Background color
                padding: '2px 4px',
                fontWeight: 'bold',
                borderBottomRightRadius: '4px',
              }}
            >
              {line.text}
            </div>
          </div>
        );
      })
    );
  };




  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <Image
        src={src || ''}
        alt={alt || 'No image available'}
        style={{ cursor: 'pointer', width: '100%', borderRadius: '5px' }}
        onClick={showInLightBox ? onClick : undefined}
        onLoad={handleImageLoad}
      />
      {renderBoxes()}
      {renderSmartCrop()}
      {renderPeopleBoxes()}
      {renderObjectBoxes()}
      {renderReadResults()}
    </div>
  );
};

ImageWithAnnotations.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  showInLightBox: PropTypes.bool,
  onClick: PropTypes.func,
  denseCaptions: PropTypes.shape({ values: PropTypes.array }),
  smartCrops: PropTypes.object,
  peopleResult: PropTypes.arrayOf(
    PropTypes.shape({
      boundingBox: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
      }).isRequired,
    })
  ),
  objectsResult: PropTypes.arrayOf(
    PropTypes.shape({
      boundingBox: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
      }).isRequired,
    })
  ),
  readResult:PropTypes.array,
  imageSize: PropTypes.string.isRequired,
};

ImageWithAnnotations.defaultProps = {
  alt: 'Image',
  showInLightBox: false,
  onClick: undefined,
  denseCaptions: null,
  smartCrops: null,
  peopleResult: null,
  objectsResult: null,
  readResult: null,
};

export default ImageWithAnnotations;
